'use client'

import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { isConfigured, authSignUp, authSignIn, authSignOut, authRefresh } from '@/lib/supabase'
import { saveUserProfile, getUserProfile } from '@/lib/userProfile'

const AuthContext = createContext(null)
const SESSION_KEY = 'fairfares_session'
const AUTH_COOKIE = 'ff_auth'
function setAuthCookie(active) {
  if (typeof document === 'undefined') return
  if (active) document.cookie = AUTH_COOKIE + '=1; path=/; max-age=86400; SameSite=Strict'
  else document.cookie = AUTH_COOKIE + '=; path=/; max-age=0'
}


const FB_CONFIG = {
  apiKey:            process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain:        process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
}

async function getFirebaseApp() {
  if (!FB_CONFIG.apiKey) return null
  const { initializeApp, getApps, getApp } = await import('firebase/app')
  return getApps().length ? getApp() : initializeApp(FB_CONFIG)
}

function readSession() {
  try {
    const s = localStorage.getItem(SESSION_KEY)
    return s ? JSON.parse(s) : null
  } catch { return null }
}

function writeSession(data) {
  try {
    if (data) localStorage.setItem(SESSION_KEY, JSON.stringify(data))
    else localStorage.removeItem(SESSION_KEY)
  } catch {}
}

/** Add .uid alias so existing callers don't break */
const norm = (u) => u ? { ...u, uid: u.id ?? u.uid ?? u.email } : null

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    if (typeof window === 'undefined') return null
    const s = readSession()
    return s?.user ? norm(s.user) : null
  })
  const [accessToken, setAccessToken] = useState(() => {
    if (typeof window === 'undefined') return null
    const s = readSession()
    return s?.access_token ?? null
  })
  const [isNewUser, setIsNewUser]     = useState(false)
  const pendingGoogleCred             = useRef(null)

  function applySession(data, { isNew = false } = {}) {
    const user         = norm(data.user ?? data)
    const token        = data.access_token ?? null
    const refreshToken = data.refresh_token ?? null
    writeSession({ user, access_token: token, refresh_token: refreshToken })
    setAuthCookie(true)
    setCurrentUser(user)
    setAccessToken(token)
    // Sync tier to HttpOnly server cookie — cannot be spoofed by client JS
    if (token) fetch('/api/auth/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, isNew }) }).catch(() => {})
    return { user, token }
  }

  async function tryRefreshToken() {
    const s = readSession()
    if (!s?.access_token) return

    // Check JWT expiry locally
    let secsLeft = Infinity
    try {
      const b64 = s.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
      const { exp } = JSON.parse(atob(b64))
      secsLeft = exp - Math.floor(Date.now() / 1000)
    } catch {}

    if (secsLeft > 300) return // Still >5 min valid

    if (!s.refresh_token) {
      // Token expired, no refresh token — force sign-out
      writeSession(null)
      setAuthCookie(false)
      setCurrentUser(null)
      setAccessToken(null)
      return
    }

    try {
      const data = await authRefresh(s.refresh_token)
      if (data?.access_token) applySession(data)
    } catch {}
  }

  // On mount: check token expiry and refresh; schedule future refresh
  useEffect(() => {
    const s = readSession()
    if (s?.user) setAuthCookie(true)

    tryRefreshToken()

    const interval = setInterval(tryRefreshToken, 50 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  async function signInWithGoogle() {
    const app = await getFirebaseApp()
    if (!app) {
      // Dev fallback — no Firebase config
      const user = norm({ id: 'google-dev', email: 'google@dev.local', displayName: 'Dev User' })
      writeSession({ user, access_token: null })
      setCurrentUser(user)
      setAccessToken(null)
      return user
    }

    const { getAuth, GoogleAuthProvider, signInWithPopup, EmailAuthProvider, signInWithCredential, linkWithCredential } = await import('firebase/auth')
    const { getFirestore, doc, getDoc, setDoc } = await import('firebase/firestore')
    const fbAuth   = getAuth(app)
    const provider = new GoogleAuthProvider()

    let result
    try {
      result = await signInWithPopup(fbAuth, provider)
    } catch (err) {
      if (err.code === 'auth/account-exists-with-different-credential') {
        // Store credential so signIn() can complete the link
        pendingGoogleCred.current = GoogleAuthProvider.credentialFromError(err)
        throw new Error(
          'Dit e-mailadres is al gekoppeld aan een andere inlogmethode. ' +
          'Log in met e-mail en wachtwoord — je Google-account wordt daarna automatisch gekoppeld.'
        )
      }
      throw err
    }

    const fbUser  = result.user
    const idToken = await fbUser.getIdToken()
    const user    = norm({ id: fbUser.uid, email: fbUser.email, displayName: fbUser.displayName })
    writeSession({ user, access_token: idToken })
    setCurrentUser(user)
    setAccessToken(idToken)

    // Create Firestore user doc on first login
    const db   = getFirestore(app)
    const ref  = doc(db, 'users', fbUser.uid)
    const snap = await getDoc(ref)
    if (!snap.exists()) {
      await setDoc(ref, {
        homeAirport:  'CDG',
        premium:      false,
        tier:         'free',
        email:        fbUser.email,
        displayName:  fbUser.displayName ?? null,
      })
      setIsNewUser(true)
      fetch('/api/auth/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: idToken, isNew: true }) }).catch(() => {})
      // Send account welcome email (fire-and-forget; server-side via API route)
      fetch('/api/welcome-email', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          email:     fbUser.email,
          firstName: fbUser.displayName?.split(' ')[0] ?? null,
        }),
      }).catch(() => {})
    }

    return user
  }

  async function signUp(email, password) {
    if (!email || !password) throw new Error('Email and password are required')

    if (!isConfigured()) {
      // Dev fallback — no Supabase credentials set
      const user = norm({ id: email, email })
      writeSession({ user, access_token: null })
      setCurrentUser(user)
      setAccessToken(null)
      await saveUserProfile(user.uid, { homeAirport: 'CDG', flexDays: 3, emailAlerts: false, premium: false, tier: 'free' })
      return user
    }

    const data     = await authSignUp(email, password)
    const { user, token } = applySession(data, { isNew: true })
    const existingProfile = await getUserProfile(user.uid, token).catch(() => null)
    if (!existingProfile) {
      await saveUserProfile(user.uid, { homeAirport: 'CDG', flexDays: 3, emailAlerts: false, premium: false, tier: 'free' }, token)
    }
    setIsNewUser(true)

    // Register referral if user arrived via a ?ref= link — read from cookie, store in DB
    try {
      const match = document.cookie.match(/(?:^|; )ff_referrer=([^;]*)/)
      const refCode = match ? decodeURIComponent(match[1]) : null
      if (refCode && /^[a-f0-9]{8}$/i.test(refCode) && user.uid) {
        fetch('/api/referral', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'register', referrerCode: refCode, newUserId: user.uid, token }),
        }).catch(() => {})
        document.cookie = 'ff_referrer=; max-age=0; path=/'
      }
    } catch {}

    return user
  }

  async function signIn(email, password) {
    if (!email || !password) throw new Error('Email and password are required')

    let user
    if (!isConfigured()) {
      user = norm({ id: email, email })
      writeSession({ user, access_token: null })
      setCurrentUser(user)
      setAccessToken(null)
    } else {
      const data = await authSignIn(email, password)
      user = applySession(data).user
    }

    // Complete pending Google account link if present
    if (pendingGoogleCred.current) {
      const app = await getFirebaseApp()
      if (app) {
        try {
          const { getAuth, EmailAuthProvider, signInWithCredential, linkWithCredential } = await import('firebase/auth')
          const fbAuth    = getAuth(app)
          const emailCred = EmailAuthProvider.credential(email, password)
          const userCred  = await signInWithCredential(fbAuth, emailCred)
          await linkWithCredential(userCred.user, pendingGoogleCred.current)
        } catch { /* best-effort */ }
      }
      pendingGoogleCred.current = null
    }

    return user
  }

  async function signOut() {
    if (accessToken) try { await authSignOut(accessToken) } catch {}
    setAuthCookie(false)
    writeSession(null)
    setCurrentUser(null)
    setAccessToken(null)
    fetch('/api/auth/sync', { method: 'DELETE' }).catch(() => {})
  }

  return (
    <AuthContext.Provider value={{ currentUser, accessToken, signIn, signUp, signOut, signInWithGoogle, isNewUser, clearNewUser: () => setIsNewUser(false) }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
